<template>
  <div class="rn-team-area team-area rn-section-gap bg_color--1">
    <v-container>
      <template v-for="(group, groupIndex) in getGroupedTeam()" :key="groupIndex">
        <v-row v-if="group.Categoria" class="mt-8 mb-4">
          <v-col cols="12">
            <h2 class="team-category-title mb-2">{{ group.Categoria }}</h2>
            <v-divider class="mb-6"></v-divider>
          </v-col>
        </v-row>
        
        <v-row class="row--10" align="stretch">
          <v-col
            :cols="12"
            :sm="comNumColumnas == 12 ? 12 : 6"
            :md="comNumColumnas"
            v-for="team in group.Members"
            :key="team.id"
            class="mb-10"
          >
            <div class="team-card h-100 d-flex flex-column">
              <!-- Imagen -->
              <div class="thumbnail mb-4" v-if="team.Imagen">
                <img :src="imagenIDtoSRC(team.Imagen)" :alt="'Fotografía de ' + team.Nombre" />
              </div>

              <!-- Contenido -->
              <div class="inner d-flex flex-column flex-grow-1">
                <h3 v-if="team.Nombre" class="mb-1">{{ team.Nombre }}</h3>
                <h4 v-if="team.Puesto" class="mb-3">{{ team.Puesto }}</h4>
                <p v-if="team.Descripcion" v-html="team.Descripcion.replace(/(?:\r\n|\r|\n)/g, '<br>')" class="flex-grow-1" />

                <!-- Links: email y extensión -->
                <div class="team-links mt-auto pt-3" v-if="team.Email || team.Extension">
                  <a
                    v-if="team.Email"
                    :href="'mailto:' + team.Email"
                    :title="'Enviar email a ' + team.Nombre"
                    class="link-item"
                  >
                    <v-icon :color="estiloTarjetaTituloColor">mdi-email-outline</v-icon>
                    <span class="ml-2">{{ team.Email }}</span>
                  </a>
                  <a
                    v-if="team.Extension"
                    :href="'tel:' + (NOTARIA?.Contacto?.Telefono ? NOTARIA.Contacto.Telefono + '#' : '') + team.Extension"
                    :title="'Llamar extensión ' + team.Extension"
                    class="link-item"
                  >
                    <v-icon :color="estiloTarjetaTituloColor">mdi-phone-outline</v-icon>
                    <span class="ml-2">Ext. {{ team.Extension }}</span>
                  </a>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { useStore } from '../../store/index'
const store = useStore()
const { NOTARIA } = storeToRefs(store)
import { useUtils } from '#imports';
const { imagenIDtoSRC } = useUtils()
import { useStyles } from '../../store/styles';
const style = useStyles();

function getGroupedTeam() {
  const team = getTeamSort();
  if (team.length === 0) return [];
  
  const groups: { Categoria: string, Members: any[] }[] = [];
  const map = new Map<string, any[]>();
  
  // Agrupar por Categoría
  team.forEach(member => {
    const cat = (member.Categoria || '').trim();
    if (!map.has(cat)) {
      map.set(cat, []);
      groups.push({ Categoria: cat, Members: map.get(cat)! });
    }
    map.get(cat)!.push(member);
  });
  
  return groups;
}

function getTeamSort(): Array<any> {
  const conPos = [];
  const sinPos = [];
  if (!NOTARIA.value || !NOTARIA.value.Team || NOTARIA.value.Team.length === 0) {
    return [];
  }

  for (const item of NOTARIA.value.Team) {
    if (item.Posicion !== undefined && item.Posicion !== null) {
      conPos.push(item);
    } else {
      sinPos.push(item);
    }
  }

  if (conPos.length === 0) {
    return NOTARIA.value.Team;
  }

  conPos.sort((a, b) => a.Posicion - b.Posicion);
  return [...conPos, ...sinPos];
}

const comNumColumnas = computed(() => {
  const cols = Number(NOTARIA.value?.Theme?.Team?.NumeroColumnas || 3);
  if (cols == 1) return 12;
  if (cols == 2) return 6;
  if (cols == 3) return 4;
  if (cols == 4) return 3;
  return 4;
})

const estiloTarjetaTituloColor    = style.ThemeSectionTeam.Tarjeta.Titulo.css.Color
const estiloTarjetaTituloShadow   = style.ThemeSectionTeam.Tarjeta.Titulo.css.BackgroundColor ? "4px 2px 6px " + style.ThemeSectionTeam.Tarjeta.Titulo.css.BackgroundColor : ""
const estiloTarjetaTituloFont     = style.ThemeSectionTeam.Tarjeta.Titulo.css.Font
const estiloTarjetaTituloSize     = style.ThemeSectionTeam.Tarjeta.Titulo.css.Size
const estiloTarjetaTituloWeight   = style.ThemeSectionTeam.Tarjeta.Titulo.css.Weight
const estiloTarjetaTituloAlign    = style.ThemeSectionTeam.Tarjeta.Titulo.css.Align

const estiloTarjetaDescripcionColor  = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Color
const estiloTarjetaDescripcionShadow = style.ThemeSectionTeam.Tarjeta.Descripcion.css.BackgroundColor ? "4px 2px 6px " + style.ThemeSectionTeam.Tarjeta.Descripcion.css.BackgroundColor : ""
const estiloTarjetaDescripcionFont   = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Font
const estiloTarjetaDescripcionSize   = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Size
const estiloTarjetaDescripcionWeight = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Weight
const estiloTarjetaDescripcionAlign  = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Align

const estiloFlexAlign = computed(() => {
  const align = style.ThemeSectionTeam.Tarjeta.Descripcion.css.Align;
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
});
</script>

<style lang="scss">
.team-card {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }

  .thumbnail {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 8px;

    img {
      max-width: 100%;
      width: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;

      &:hover {
        transform: scale(1.04);
      }
    }
  }

  h3 {
    color: v-bind(estiloTarjetaTituloColor) !important;
    text-shadow: v-bind(estiloTarjetaTituloShadow) !important;
    font-size: v-bind(estiloTarjetaTituloSize) !important;
    font-family: v-bind(estiloTarjetaTituloFont) !important;
    font-weight: v-bind(estiloTarjetaTituloWeight) !important;
    text-align: v-bind(estiloTarjetaTituloAlign) !important;
  }

  h4 {
    color: v-bind(estiloTarjetaDescripcionColor) !important;
    font-family: v-bind(estiloTarjetaDescripcionFont) !important;
    font-size: v-bind(estiloTarjetaDescripcionSize) !important;
    font-weight: v-bind(estiloTarjetaDescripcionWeight) !important;
    text-align: v-bind(estiloTarjetaDescripcionAlign) !important;
    opacity: 0.8;
  }

  p {
    color: v-bind(estiloTarjetaDescripcionColor) !important;
    text-shadow: v-bind(estiloTarjetaDescripcionShadow) !important;
    font-family: v-bind(estiloTarjetaDescripcionFont) !important;
    font-size: v-bind(estiloTarjetaDescripcionSize) !important;
    font-weight: v-bind(estiloTarjetaDescripcionWeight) !important;
    text-align: v-bind(estiloTarjetaDescripcionAlign) !important;
  }
}

.team-links {
  display: flex;
  flex-direction: column;
  align-items: v-bind(estiloFlexAlign);
  gap: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.link-item {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s;
  color: v-bind(estiloTarjetaTituloColor) !important;

  &:hover {
    opacity: 0.7;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    word-break: break-all;
  }
}

.team-category-title {
  color: v-bind(estiloTarjetaTituloColor) !important;
  text-shadow: v-bind(estiloTarjetaTituloShadow) !important;
  font-family: v-bind(estiloTarjetaTituloFont) !important;
  font-size: 1.8rem;
  font-weight: 700;
  text-transform: uppercase;
}
</style>
